import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  FolderIcon,
  DocumentTextIcon,
  PlayCircleIcon,
  QuestionMarkCircleIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  DocumentIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline'
import { AdminApi, AdminCourseContent } from '../../services/adminApi'
import CourseContentEditorModal from '../../components/admin/CourseContentEditorModal'
import CourseContentCopyFromModal from '../../components/admin/CourseContentCopyFromModal'
import AdminTestViewer from '../../components/admin/AdminTestViewer'
import CourseMaterialModal from '../../components/admin/CourseMaterialModal'
import DocumentViewer, {
  type WhiteboardEditRequest,
} from '../../components/admin/DocumentViewer'
import MaterialWhiteboardList from '../../components/learning/MaterialWhiteboardList'
import SampleProjectViewer from '../../components/materials/SampleProjectViewer'
import CommentSection from '../../components/CommentSection'
import AdminCourseCommentsTab from '../../components/admin/AdminCourseCommentsTab'
import AdminCourseHomeworkManager from '../../components/admin/AdminCourseHomeworkManager'
import AdminCourseSessionManager from '../../components/admin/AdminCourseSessionManager'
import AdminClassroomManager from '../../components/admin/AdminClassroomManager'
import CourseSkillAttach from '../../components/skills/CourseSkillAttach'
import HtmlWithMermaid from '../../components/learning/HtmlWithMermaid'
import { toast } from 'react-toastify'
import apiClient from '../../services/apiClient'
import SecureVideo from '../../components/learning/SecureVideo'
import {
  buildPlaybackStreamUrl,
  resolvePlaybackFormat,
  type PlaybackFormat,
} from '../../hooks/useSecurePlaybackStream'

type ContentType = 'VIDEO' | 'POST' | 'FORDER' | 'TEST'

const isFolderType = (type?: string | null) => {
  const t = String(type || '').toUpperCase()
  return t === 'FORDER' || t === 'FOLDER'
}

const normalizeParentId = (value: unknown): number | null => {
  if (value == null || value === '' || value === 0 || value === '0') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const normalizeContentItem = (raw: any): AdminCourseContent => {
  const contentTypeRaw = String(raw.contentType ?? raw.content_type ?? 'POST').toUpperCase()
  // Keep DB typo FORDER for writes; treat FOLDER as folder in UI
  let contentType = contentTypeRaw as AdminCourseContent['contentType']
  if (contentTypeRaw === 'FOLDER') contentType = 'FORDER'
  if (contentTypeRaw === 'QUIZ' || contentTypeRaw === 'QUIZZ') contentType = 'TEST'

  return {
    id: Number(raw.id),
    hashId: raw.hashId ?? raw.hash_id,
    courseId: Number(raw.courseId ?? raw.course_id),
    contentType,
    name: raw.name,
    detail: raw.detail ?? undefined,
    objectId:
      raw.objectId != null
        ? Number(raw.objectId)
        : raw.object_id != null
          ? Number(raw.object_id)
          : null,
    parentId: normalizeParentId(raw.parentId ?? raw.parent_id),
    position: raw.position != null ? Number(raw.position) : 0,
    isPublic: raw.isPublic ?? raw.is_public,
    status: raw.status,
    createdAt: raw.createdAt ?? raw.created_at,
    updatedAt: raw.updatedAt ?? raw.updated_at,
  }
}

const CONTENT_TYPE_OPTIONS: { type: ContentType; label: string; icon: typeof FolderIcon }[] = [
  { type: 'FORDER', label: 'Thư mục', icon: FolderIcon },
  { type: 'POST', label: 'Bài viết', icon: DocumentTextIcon },
  { type: 'VIDEO', label: 'Video', icon: PlayCircleIcon },
  { type: 'TEST', label: 'Quiz', icon: QuestionMarkCircleIcon },
]

const AdminCourseDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const courseId = parseInt(id || '0')
  const [activeTab, setActiveTab] = useState<'content' | 'sessions' | 'live' | 'materials' | 'comments' | 'homeworks'>('content')
  const [courseName, setCourseName] = useState('')
  const [contentFromCourseId, setContentFromCourseId] = useState<number | null>(null)
  const [contentSourceCourseName, setContentSourceCourseName] = useState<string | null>(null)

  const [contentList, setContentList] = useState<AdminCourseContent[]>([])
  const [loadingContent, setLoadingContent] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<AdminCourseContent | undefined>()
  const [selectedContentType, setSelectedContentType] = useState<ContentType>('POST')
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null)
  const [selectedItem, setSelectedItem] = useState<AdminCourseContent | null>(null)
  const [addMenuParentId, setAddMenuParentId] = useState<number | null | 'root'>(null)
  const [copyModalOpen, setCopyModalOpen] = useState(false)
  const [copyTargetParentId, setCopyTargetParentId] = useState<number | null>(null)
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(new Set())
  const [videoStreamUrl, setVideoStreamUrl] = useState<string | null>(null)
  const [videoStreamFormat, setVideoStreamFormat] = useState<PlaybackFormat>('mp4')
  const [videoStreamError, setVideoStreamError] = useState<string | null>(null)
  const previewVideoRef = useRef<HTMLVideoElement | null>(null)
  const [focusCommentId, setFocusCommentId] = useState<number | null>(null)

  const [materials, setMaterials] = useState<any[]>([])
  const [loadingMaterials, setLoadingMaterials] = useState(false)
  const [materialModalOpen, setMaterialModalOpen] = useState(false)
  const [selectedMaterial, setSelectedMaterial] = useState<any | null>(null)
  const [materialsExpanded, setMaterialsExpanded] = useState(true)
  const [skillsExpanded, setSkillsExpanded] = useState(false)
  const [materialSearch, setMaterialSearch] = useState('')
  const [materialGroupTab, setMaterialGroupTab] = useState<'Slide' | 'Tài liệu tham khảo' | 'Khác' | 'Dự án mẫu'>('Slide')
  const [courseSessions, setCourseSessions] = useState<{ id: number; title: string; orderIndex: number }[]>([])
  const [materialSessionFilter, setMaterialSessionFilter] = useState<string>('all')
  const [updatingMaterialSession, setUpdatingMaterialSession] = useState(false)
  const [whiteboardRefresh, setWhiteboardRefresh] = useState(0)
  const [whiteboardEditRequest, setWhiteboardEditRequest] =
    useState<WhiteboardEditRequest | null>(null)

  const MATERIAL_GROUP_TABS = ['Slide', 'Tài liệu tham khảo', 'Khác', 'Dự án mẫu'] as const

  const fetchCourse = async () => {
    if (!courseId) return
    try {
      const res = await AdminApi.getCourseById(courseId)
      if (res.success) {
        setCourseName(res.data.name)
        const sharedId =
          res.data.contentFromCourseId != null && Number(res.data.contentFromCourseId) > 0
            ? Number(res.data.contentFromCourseId)
            : null
        setContentFromCourseId(sharedId)
        if (sharedId) {
          try {
            const source = await AdminApi.getCourseById(sharedId)
            if (source.success) setContentSourceCourseName(source.data.name)
            else setContentSourceCourseName(null)
          } catch {
            setContentSourceCourseName(null)
          }
        } else {
          setContentSourceCourseName(null)
        }
      }
    } catch (error) {
      console.error('Failed to fetch course', error)
    }
  }

  const fetchContent = async () => {
    if (!courseId) return
    setLoadingContent(true)
    try {
      const res = await AdminApi.getCourseContent(courseId)
      if (res.success) {
        const normalized = (res.data || []).map(normalizeContentItem)
        setContentList(normalized)
        const sharedId =
          res.contentFromCourseId != null && Number(res.contentFromCourseId) > 0
            ? Number(res.contentFromCourseId)
            : null
        if (sharedId) setContentFromCourseId(sharedId)
        setSelectedItem((prev) => {
          if (!prev) return null
          return normalized.find((c) => c.id === prev.id) || null
        })
      }
    } catch (error) {
      console.error('Failed to fetch course content', error)
      toast.error('Lỗi khi tải nội dung khóa học')
    } finally {
      setLoadingContent(false)
    }
  }

  const fetchMaterials = async () => {
    if (!courseId) return
    setLoadingMaterials(true)
    try {
      const res = await fetch(`/api/v1/admin/courses/${id}/materials`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      })
      const data = await res.json()
      if (data.success) {
        setMaterials(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch materials', error)
      toast.error('Lỗi khi tải danh sách tài liệu')
    } finally {
      setLoadingMaterials(false)
    }
  }

  const fetchCourseSessions = async () => {
    if (!courseId) return
    try {
      const res = await fetch(`/api/v1/admin/courses/${id}/sessions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      })
      const data = await res.json()
      if (data.success) {
        setCourseSessions(
          (data.data || []).map((s: any) => ({
            id: Number(s.id),
            title: s.title,
            orderIndex: Number(s.orderIndex ?? s.order_index ?? 0),
          }))
        )
      }
    } catch (error) {
      console.error('Failed to fetch course sessions', error)
    }
  }

  const handleUpdateMaterialSession = async (materialId: number, nextSessionId: string) => {
    setUpdatingMaterialSession(true)
    try {
      const body =
        nextSessionId === '' || nextSessionId === 'null'
          ? { sessionId: null }
          : { sessionId: Number(nextSessionId) }
      const res = await fetch(`/api/v1/admin/courses/materials/${materialId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Đã cập nhật buổi học cho tài liệu')
        setMaterials((prev) =>
          prev.map((m) => (m.id === materialId ? { ...m, ...data.data } : m))
        )
        setSelectedMaterial((prev: any) =>
          prev?.id === materialId ? { ...prev, ...data.data } : prev
        )
      } else {
        toast.error(data.message || 'Cập nhật thất bại')
      }
    } catch (error) {
      console.error('Failed to update material session', error)
      toast.error('Lỗi khi gắn buổi học')
    } finally {
      setUpdatingMaterialSession(false)
    }
  }

  useEffect(() => {
    fetchCourse()
    setExpandedFolderIds(new Set())
    setSelectedItem(null)
  }, [courseId])

  useEffect(() => {
    if (activeTab === 'content') {
      fetchContent()
    } else if (activeTab === 'materials') {
      fetchMaterials()
      fetchCourseSessions()
    }
  }, [activeTab, id])

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('[data-add-menu]')) return
      setAddMenuParentId(null)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const openAddModal = (type: ContentType, parentId: number | null = null) => {
    setEditingItem(undefined)
    setSelectedContentType(type)
    setSelectedParentId(parentId)
    setAddMenuParentId(null)
    setEditorOpen(true)
  }

  const openCopyFromModal = (parentId: number | null = null) => {
    setCopyTargetParentId(parentId)
    setAddMenuParentId(null)
    setCopyModalOpen(true)
  }

  const toggleFolderExpand = (folderId: number) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const handleCopySuccess = async () => {
    setExpandedFolderIds(new Set())
    await fetchContent()
  }

  const openEditModal = async (item: AdminCourseContent) => {
    const t = String(item.contentType || '').toUpperCase()
    const normalizedType: ContentType =
      t === 'FOLDER' || t === 'FORDER'
        ? 'FORDER'
        : t === 'QUIZ' || t === 'QUIZZ'
          ? 'TEST'
          : (['VIDEO', 'POST', 'TEST'].includes(t) ? (t as ContentType) : 'POST')
    setSelectedContentType(normalizedType)
    setSelectedParentId(normalizeParentId(item.parentId))

    // List endpoint omits detail — load full item before opening editor
    if ((normalizedType === 'POST' || normalizedType === 'VIDEO') && !item.detail) {
      try {
        const res = await AdminApi.getCourseContentItem(item.id)
        if (res.success && res.data) {
          const full = normalizeContentItem(res.data)
          setEditingItem(full)
          setEditorOpen(true)
          return
        }
      } catch (e) {
        console.error(e)
      }
    }
    setEditingItem(item)
    setEditorOpen(true)
  }

  const selectContentItem = async (item: AdminCourseContent) => {
    setSelectedItem(item)
    const needsDetail =
      (item.contentType === 'POST' || item.contentType === 'VIDEO') && item.detail == null
    if (!needsDetail) return
    try {
      const res = await AdminApi.getCourseContentItem(item.id)
      if (res.success && res.data) {
        const full = normalizeContentItem(res.data)
        setSelectedItem(full)
        setContentList((prev) => prev.map((c) => (c.id === full.id ? { ...c, detail: full.detail } : c)))
      }
    } catch (e) {
      console.error('Failed to load content detail', e)
    }
  }

  const openCommentInContent = async (courseContentId: number, commentId: number) => {
    setFocusCommentId(commentId)
    setActiveTab('content')
    setLoadingContent(true)
    try {
      const res = await AdminApi.getCourseContent(courseId)
      if (!res.success) {
        toast.error('Không tải được nội dung khóa học')
        return
      }
      const normalized = (res.data || []).map(normalizeContentItem)
      setContentList(normalized)
      const sharedId =
        res.contentFromCourseId != null && Number(res.contentFromCourseId) > 0
          ? Number(res.contentFromCourseId)
          : null
      if (sharedId) setContentFromCourseId(sharedId)
      const item = normalized.find((c) => c.id === courseContentId)
      if (!item) {
        toast.error('Không tìm thấy bài học của bình luận')
        return
      }
      await selectContentItem(item)
      window.setTimeout(() => {
        document.getElementById('admin-lesson-comments')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 250)
    } catch (error) {
      console.error('Failed to open comment lesson', error)
      toast.error('Lỗi khi mở bài học từ bình luận')
    } finally {
      setLoadingContent(false)
    }
  }

  const handleDeleteContent = async (itemId: number) => {
    const item = contentList.find((c) => c.id === itemId)
    const isFolder = item ? isFolderType(item.contentType) : false

    // Đếm mọi descendant (folder lồng + bài viết) để cảnh báo trước khi cascade soft-delete
    const collectDescendantIds = (rootId: number): number[] => {
      const ids: number[] = []
      const seen = new Set<number>([rootId])
      const queue = [rootId]
      while (queue.length > 0) {
        const parentId = queue.shift()!
        for (const c of contentList) {
          if (normalizeParentId(c.parentId) === parentId && !seen.has(c.id)) {
            seen.add(c.id)
            ids.push(c.id)
            queue.push(c.id)
          }
        }
      }
      return ids
    }

    const descendantIds = isFolder ? collectDescendantIds(itemId) : []
    const confirmMsg =
      isFolder && descendantIds.length > 0
        ? `Xóa thư mục này sẽ xóa luôn ${descendantIds.length} nội dung bên trong (bài viết/thư mục con). Bạn có chắc?`
        : 'Bạn có chắc chắn muốn xóa nội dung này?'

    if (!window.confirm(confirmMsg)) return
    try {
      const res = await AdminApi.deleteCourseContent(itemId)
      if (res.success) {
        toast.success(res.message || 'Đã xóa thành công')
        const deletedSet = new Set([itemId, ...descendantIds])
        if (selectedItem && deletedSet.has(selectedItem.id)) {
          setSelectedItem(null)
        }
        fetchContent()
      } else {
        toast.error(res.message || 'Lỗi khi xóa')
      }
    } catch (error: any) {
      console.error('Delete error', error)
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Có lỗi xảy ra'
      toast.error(msg)
    }
  }

  const getSiblings = (parentId: number | null) => {
    return contentList
      .filter((item) => normalizeParentId(item.parentId) === parentId)
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id)
  }

  const handleMoveContent = async (item: AdminCourseContent, direction: 'up' | 'down') => {
    const parentId = normalizeParentId(item.parentId)
    const siblings = getSiblings(parentId)
    const index = siblings.findIndex((s) => s.id === item.id)
    if (index < 0) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === siblings.length - 1) return

    const swapIndex = direction === 'up' ? index - 1 : index + 1
    const reordered = [...siblings]
    ;[reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]]
    const orderedIds = reordered.map((s) => s.id)

    setContentList((prev) =>
      prev.map((c) => {
        const newPos = orderedIds.indexOf(c.id)
        if (newPos >= 0) return { ...c, position: newPos }
        return c
      })
    )

    try {
      const res = await AdminApi.reorderCourseContent(courseId, orderedIds, parentId)
      if (!res.success) {
        toast.error(res.message || 'Lỗi khi cập nhật thứ tự')
        fetchContent()
      }
    } catch (e) {
      console.error(e)
      toast.error('Lỗi khi cập nhật thứ tự')
      fetchContent()
    }
  }

  const handleDeleteMaterial = async (materialId: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa tài liệu này?')) return
    try {
      const res = await fetch(`/api/v1/admin/courses/materials/${materialId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Đã xóa thành công')
        if (selectedMaterial?.id === materialId) setSelectedMaterial(null)
        fetchMaterials()
      } else {
        toast.error(data.message || 'Lỗi khi xóa tài liệu')
      }
    } catch (error) {
      console.error('Delete material error', error)
      toast.error('Có lỗi xảy ra khi xóa tài liệu')
    }
  }

  const groupMaterialsByType = () => {
    const groups: Record<string, any[]> = {
      Slide: [],
      'Tài liệu tham khảo': [],
      Khác: [],
      'Dự án mẫu': [],
    }

    materials.forEach((mat) => {
      const t = String(mat.type || '').toLowerCase()
      if (t === 'sample_project' || t.includes('sample_project')) {
        groups['Dự án mẫu'].push(mat)
      } else if (t === 'slide' || t.includes('slide')) {
        groups.Slide.push(mat)
      } else if (
        t === 'reference' ||
        t.includes('reference') ||
        t.includes('tham khảo') ||
        t.includes('tham khao')
      ) {
        groups['Tài liệu tham khảo'].push(mat)
      } else {
        groups.Khác.push(mat)
      }
    })

    return groups
  }

  const filterMaterialsBySearch = (items: any[]) => {
    const q = materialSearch.trim().toLowerCase()
    if (!q) return items
    return items.filter((mat) => {
      const hay = `${mat.title || ''} ${mat.fileType || ''} ${mat.type || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }

  const handleMoveMaterial = async (matId: number, direction: 'up' | 'down') => {
    const currentIndex = materials.findIndex((m) => m.id === matId)
    if (currentIndex < 0) return

    const currentMat = materials[currentIndex]
    const groups = groupMaterialsByType()
    let targetGroupItems: any[] = []
    const t = String(currentMat.type || '').toLowerCase()
    if (t === 'sample_project' || t.includes('sample_project')) {
      targetGroupItems = groups['Dự án mẫu']
    } else if (currentMat.type === 'Slide' || t.includes('slide')) {
      targetGroupItems = groups['Slide']
    } else if (currentMat.type === 'Tài liệu tham khảo' || t.includes('tham khảo') || t.includes('reference')) {
      targetGroupItems = groups['Tài liệu tham khảo']
    } else {
      targetGroupItems = groups['Khác']
    }

    const indexInGroup = targetGroupItems.findIndex((m) => m.id === matId)
    if (direction === 'up' && indexInGroup === 0) return
    if (direction === 'down' && indexInGroup === targetGroupItems.length - 1) return

    const swapMat = direction === 'up' ? targetGroupItems[indexInGroup - 1] : targetGroupItems[indexInGroup + 1]
    const swapIndex = materials.findIndex((m) => m.id === swapMat.id)

    const newMaterials = [...materials]
    const temp = newMaterials[currentIndex]
    newMaterials[currentIndex] = newMaterials[swapIndex]
    newMaterials[swapIndex] = temp

    setMaterials(newMaterials)

    try {
      await fetch(`/api/v1/admin/courses/${courseId}/materials/reorder`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderedIds: newMaterials.map((m) => m.id) }),
      })
    } catch (e) {
      console.error('Lỗi khi lưu vị trí', e)
      toast.error('Lỗi khi lưu thứ tự tài liệu')
    }
  }

  const getIcon = (type: string) => {
    const t = String(type || '').toUpperCase()
    switch (t) {
      case 'FORDER':
      case 'FOLDER':
        return <FolderIcon className="h-5 w-5 text-amber-500" />
      case 'VIDEO':
        return <PlayCircleIcon className="h-5 w-5 text-red-500" />
      case 'TEST':
      case 'QUIZ':
      case 'QUIZZ':
        return <QuestionMarkCircleIcon className="h-5 w-5 text-green-500" />
      case 'POST':
        return <DocumentTextIcon className="h-5 w-5 text-m2-primary" />
      default:
        return <DocumentIcon className="h-5 w-5 text-m2-on-surface-variant" />
    }
  }

  const getChildren = (parentId: number | null) => getSiblings(parentId)

  useEffect(() => {
    let mounted = true
    const loadVideoStream = async () => {
      if (!selectedItem?.objectId || selectedItem.contentType !== 'VIDEO') {
        if (mounted) {
          setVideoStreamUrl(null)
          setVideoStreamFormat('mp4')
          setVideoStreamError(null)
        }
        return
      }
      try {
        setVideoStreamError(null)
        setVideoStreamUrl(null)
        setVideoStreamFormat('mp4')
        const response = await apiClient.get('/student/playback/token', {
          params: { type: 'video', id: selectedItem.objectId },
        })
        if (mounted && response.data.success) {
          const base = apiClient.defaults.baseURL || '/api/v1'
          const format = resolvePlaybackFormat(response.data.data.format)
          setVideoStreamFormat(format)
          setVideoStreamUrl(
            buildPlaybackStreamUrl(base, response.data.data.playbackToken, format)
          )
        } else if (mounted) {
          setVideoStreamUrl(null)
          setVideoStreamError(response.data?.message || 'Không phát được video')
        }
      } catch (error: any) {
        console.error('Failed to load video playback token', error)
        if (mounted) {
          setVideoStreamUrl(null)
          setVideoStreamError(
            error?.response?.data?.message || 'Không tìm thấy file video trên hệ thống'
          )
        }
      }
    }
    void loadVideoStream()
    return () => {
      mounted = false
    }
  }, [selectedItem])

  const renderAddMenu = (parentKey: number | null | 'root', parentId: number | null) => {
    if (addMenuParentId !== parentKey) return null
    return (
      <div
        data-add-menu
        className="absolute right-0 top-full mt-1 z-30 w-48 bg-m2-surface-container border border-m2-outline-variant rounded-md shadow-lg py-1"
      >
        {CONTENT_TYPE_OPTIONS.map(({ type, label, icon: Icon }) => (
          <button
            key={type}
            type="button"
            onClick={() => openAddModal(type, parentId)}
            className="w-full flex items-center px-3 py-2 text-sm text-m2-on-surface hover:bg-m2-primary/10"
          >
            <Icon className="h-4 w-4 mr-2 text-m2-on-surface-variant" />
            {label}
          </button>
        ))}
        <div className="my-1 border-t border-m2-outline-variant" />
        <button
          type="button"
          onClick={() => openCopyFromModal(parentId)}
          className="w-full flex items-center px-3 py-2 text-sm text-m2-on-surface hover:bg-m2-primary/10"
        >
          <DocumentDuplicateIcon className="h-4 w-4 mr-2 text-m2-on-surface-variant" />
          Từ khóa khác…
        </button>
      </div>
    )
  }

  const renderContentItem = (item: AdminCourseContent, depth: number) => {
    const isSelected = selectedItem?.id === item.id
    const itemParentId = normalizeParentId(item.parentId)
    const siblings = getSiblings(itemParentId)
    const index = siblings.findIndex((s) => s.id === item.id)
    const isFirst = index <= 0
    const isLast = index >= siblings.length - 1
    const isFolder = isFolderType(item.contentType)
    // Always nest by parent_id so cây phân cấp vẫn hiện đúng kể cả type legacy
    const children = getChildren(item.id)
    const isExpanded = isFolder && expandedFolderIds.has(item.id)

    return (
      <div key={item.id}>
        <div
          onClick={() => selectContentItem(item)}
          className={`flex items-center justify-between py-2.5 pr-2 border-b border-m2-outline-variant hover:bg-m2-primary/10 cursor-pointer transition-colors ${
            isSelected ? 'bg-m2-primary/15 border-l-4 border-l-m2-primary' : 'border-l-4 border-l-transparent'
          } ${isFolder ? 'bg-m2-surface-high/80' : ''}`}
          style={{ paddingLeft: `${12 + depth * 20}px` }}
        >
          <div className="flex items-center space-x-1.5 min-w-0 flex-1">
            {isFolder ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleFolderExpand(item.id)
                }}
                className="p-0.5 text-m2-on-surface-variant hover:text-m2-primary rounded shrink-0"
                title={isExpanded ? 'Thu gọn' : 'Mở rộng'}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}
            {getIcon(item.contentType)}
            <span
              className={`truncate text-sm ${
                isFolder ? 'font-semibold text-m2-on-surface' : 'text-m2-on-surface-variant'
              } ${isSelected ? 'font-semibold text-m2-primary' : ''}`}
            >
              {item.name}
            </span>
          </div>
          <div className="flex items-center space-x-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => handleMoveContent(item, 'up')}
              disabled={isFirst}
              className={`p-1 rounded ${isFirst ? 'text-m2-outline-variant' : 'text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-primary'}`}
              title="Di chuyển lên"
            >
              <ArrowUpIcon className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => handleMoveContent(item, 'down')}
              disabled={isLast}
              className={`p-1 rounded ${isLast ? 'text-m2-outline-variant' : 'text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-primary'}`}
              title="Di chuyển xuống"
            >
              <ArrowDownIcon className="h-3.5 w-3.5" />
            </button>
            {isFolder && (
              <div className="relative" data-add-menu>
                <button
                  onClick={() =>
                    setAddMenuParentId(addMenuParentId === item.id ? null : item.id)
                  }
                  className="p-1 text-m2-primary hover:text-m2-primary hover:bg-m2-primary/10 rounded"
                  title="Thêm nội dung vào thư mục"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
                {renderAddMenu(item.id, item.id)}
              </div>
            )}
            <button
              onClick={() => openEditModal(item)}
              className="p-1 text-m2-primary hover:bg-m2-primary/10 rounded"
              title="Sửa"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => handleDeleteContent(item.id)}
              className="p-1 text-red-600 hover:bg-m2-error/10 rounded"
              title="Xóa"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {isFolder && isExpanded && children.map((child) => renderContentItem(child, depth + 1))}
      </div>
    )
  }

  const renderDetailPane = () => {
    if (!selectedItem) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-m2-outline mt-20">
          <DocumentIcon className="w-16 h-16 mb-4 text-m2-outline-variant" />
          <p>Chọn một bài học ở danh sách bên trái để xem chi tiết</p>
        </div>
      )
    }

    if (isFolderType(selectedItem.contentType)) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-m2-on-surface-variant mt-20">
          <FolderIcon className="w-16 h-16 mb-4 text-amber-300" />
          <p className="text-lg font-medium">{selectedItem.name}</p>
          <p className="text-sm mb-4">Thư mục chứa bài học</p>
          <div className="flex flex-wrap justify-center gap-2">
            {CONTENT_TYPE_OPTIONS.map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                type="button"
                onClick={() => openAddModal(type, selectedItem.id)}
                className="inline-flex items-center px-3 py-2 bg-m2-primary/10 text-m2-primary border border-m2-primary/30 rounded-md text-sm hover:bg-m2-primary/15"
              >
                <Icon className="h-4 w-4 mr-1.5" />
                {label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => openCopyFromModal(selectedItem.id)}
              className="inline-flex items-center px-3 py-2 bg-m2-primary/10 text-m2-primary border border-m2-primary/30 rounded-md text-sm hover:bg-m2-primary/15"
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-1.5" />
              Từ khóa khác…
            </button>
          </div>
        </div>
      )
    }

    if (selectedItem.contentType === 'TEST') {
      if (!selectedItem.objectId) {
        return (
          <div className="bg-m2-error/10 text-red-600 p-6 rounded-lg border border-m2-error/30">
            Bài quiz này chưa được liên kết với đề thi. Hãy Edit và chọn bài test.
          </div>
        )
      }
      return <AdminTestViewer paperId={selectedItem.objectId} />
    }

    return (
      <div className="bg-m2-surface-container rounded p-8 shadow-sm border border-m2-outline-variant min-h-full">
        <h2 className="text-3xl font-bold text-m2-on-surface mb-6 pb-4 border-b border-m2-outline-variant">
          {selectedItem.name}
        </h2>

        {selectedItem.contentType === 'VIDEO' && (
          <div className="mb-6">
            {selectedItem.objectId && videoStreamUrl ? (
              <SecureVideo
                key={selectedItem.objectId}
                format={videoStreamFormat}
                src={videoStreamUrl}
                videoRef={previewVideoRef}
                controls
                className="w-full max-h-[480px] rounded-lg bg-black"
              />
            ) : !selectedItem.objectId ? (
              <div className="p-4 bg-amber-50 text-amber-700 rounded-lg border border-amber-200">
                Video chưa được liên kết. Hãy Edit và chọn/upload video.
              </div>
            ) : videoStreamError ? (
              <div className="p-4 bg-m2-error/10 text-red-700 rounded-lg border border-m2-error/30">
                {videoStreamError}
              </div>
            ) : (
              <div className="p-4 bg-m2-surface-low text-m2-on-surface-variant rounded-lg border border-m2-outline-variant">
                Đang tải video...
              </div>
            )}
          </div>
        )}

        <div className="prose max-w-none text-m2-on-surface prose-img:rounded-lg prose-headings:text-m2-on-surface mb-8 html-media-center">
          {selectedItem.detail ? (
            <HtmlWithMermaid html={selectedItem.detail} className="html-media-center" />
          ) : selectedItem.contentType === 'POST' ? (
            <div className="p-8 text-center bg-m2-surface-low rounded border border-dashed border-m2-outline text-m2-outline italic">
              Bài học này chưa có nội dung chi tiết.
            </div>
          ) : null}
        </div>

        {(selectedItem.contentType === 'POST' || selectedItem.contentType === 'VIDEO') && (
          <div id="admin-lesson-comments" className="mt-8 border-t border-m2-outline-variant pt-8">
            <h3 className="text-xl font-bold text-m2-on-surface mb-6">Quản lý Bình luận</h3>
            <CommentSection courseContentId={selectedItem.id} highlightCommentId={focusCommentId} />
          </div>
        )}
      </div>
    )
  }

  const rootItems = getChildren(null)

  return (
    <div className="bg-m2-surface-container p-6 rounded-lg shadow-sm min-h-screen">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center">
          <Link to="/admin/courses" className="mr-4 text-m2-on-surface-variant hover:text-m2-on-surface transition-colors">
            <ChevronLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-bold text-m2-on-surface">
            Chi tiết khóa học: {courseName || `#${id}`}
          </h1>
        </div>
        <Link
          to={`/admin/courses/${id}/edit`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-eh-forest-ink text-eh-cream rounded-md hover:bg-eh-forest-shadow text-sm"
        >
          <PencilIcon className="h-4 w-4" />
          Sửa thông tin khóa học
        </Link>
      </div>

      <div className="border-b border-m2-outline-variant mb-6">
        <nav className="-mb-px flex space-x-8">
          {(
            [
              ['content', '📖 Nội dung khóa học'],
              ['comments', '💬 Bình luận'],
              ['homeworks', '📝 BTVN'],
              ['sessions', '📅 Buổi học'],
              ['live', '🏫 Lớp học'],
              ['materials', '📁 Tài liệu'],
            ] as const
          ).map(([tab, label]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`${
                activeTab === tab
                  ? 'border-m2-primary text-m2-primary'
                  : 'border-transparent text-m2-on-surface-variant hover:text-m2-on-surface hover:border-m2-outline'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-4 h-[calc(100vh-11rem)] min-h-[480px]">
        {activeTab === 'content' && (
          <div className="h-full flex flex-col">
            {contentFromCourseId != null && (
              <div className="mb-3 shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Khóa này dùng chung nội dung từ khóa{' '}
                <Link
                  to={`/admin/courses/${contentFromCourseId}`}
                  className="font-semibold underline hover:text-amber-700"
                >
                  #{contentFromCourseId}
                  {contentSourceCourseName ? ` — ${contentSourceCourseName}` : ''}
                </Link>
                . Mọi chỉnh sửa sẽ áp dụng cho khóa nguồn.
              </div>
            )}
            <div className="flex justify-between items-center mb-4 shrink-0">
              <h3 className="text-lg font-bold text-m2-on-surface">Curriculum (Chương trình học)</h3>
              <div className="relative" data-add-menu>
                <button
                  onClick={() => setAddMenuParentId(addMenuParentId === 'root' ? null : 'root')}
                  className="bg-m2-primary/10 text-m2-primary border border-m2-primary/30 px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-m2-primary/15 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-1.5" /> Thêm mới
                </button>
                {renderAddMenu('root', null)}
              </div>
            </div>

            {loadingContent ? (
              <div className="flex-1 flex items-center justify-center text-m2-on-surface-variant">
                Đang tải nội dung...
              </div>
            ) : contentList.length === 0 ? (
              <div className="flex-1 border rounded-xl p-12 flex flex-col items-center justify-center text-m2-on-surface-variant bg-m2-surface-low">
                <FolderIcon className="w-16 h-16 mb-4 text-m2-outline-variant" />
                <p className="text-lg">Chưa có nội dung bài học nào được tạo.</p>
                <div className="relative mt-4" data-add-menu>
                  <button
                    onClick={() => setAddMenuParentId('root')}
                    className="bg-m2-primary text-m2-on-primary px-6 py-2.5 rounded-lg hover:bg-eh-forest-shadow transition-colors inline-flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" /> Thêm mới
                  </button>
                  {renderAddMenu('root', null)}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex border rounded-xl overflow-hidden bg-m2-surface-container shadow-sm min-h-0">
                <div className="w-1/3 border-r border-m2-outline-variant bg-m2-surface-low overflow-y-auto">
                  {rootItems.map((item) => renderContentItem(item, 0))}
                </div>
                <div className="w-2/3 bg-m2-surface-low/50 p-6 overflow-y-auto">{renderDetailPane()}</div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="overflow-hidden h-full">
            <AdminCourseCommentsTab courseId={courseId} onOpenComment={openCommentInContent} />
          </div>
        )}

        {activeTab === 'homeworks' && (
          <div className="overflow-y-auto h-full pr-2">
            <AdminCourseHomeworkManager courseId={courseId} />
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="overflow-y-auto h-full pr-2">
            <AdminCourseSessionManager courseId={courseId} />
          </div>
        )}

        {activeTab === 'live' && (
          <div className="overflow-y-auto h-full pr-2">
            <AdminClassroomManager courseId={courseId} />
          </div>
        )}

        {activeTab === 'materials' && (
          <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden">
            {/* Tài liệu tham khảo */}
            <section
              className={`flex min-h-0 flex-col rounded-xl border border-m2-outline-variant bg-m2-surface-container ${
                materialsExpanded
                  ? skillsExpanded
                    ? 'min-h-0 flex-[2]'
                    : 'min-h-0 flex-1'
                  : 'shrink-0'
              }`}
            >
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-m2-outline-variant px-4 py-3">
                <button
                  type="button"
                  onClick={() => setMaterialsExpanded((v) => !v)}
                  className="flex min-w-0 items-center gap-2 text-left hover:text-m2-primary"
                >
                  {materialsExpanded ? (
                    <ChevronUpIcon className="h-5 w-5 shrink-0 text-m2-on-surface-variant" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 shrink-0 text-m2-on-surface-variant" />
                  )}
                  <h3 className="text-lg font-bold text-m2-on-surface">
                    Tài liệu tham khảo (Slides, PDF, ...)
                  </h3>
                  {!materialsExpanded && materials.length > 0 && (
                    <span className="text-xs font-normal text-m2-on-surface-variant">
                      ({materials.length} file)
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMaterialsExpanded((v) => !v)}
                    className="rounded-md border border-m2-outline px-2.5 py-1.5 text-xs text-m2-on-surface-variant hover:bg-m2-surface-high"
                  >
                    {materialsExpanded ? 'Thu gọn' : 'Mở rộng'}
                  </button>
                  <button
                    onClick={() => setMaterialModalOpen(true)}
                    className="flex items-center rounded-md border border-m2-primary/30 bg-m2-primary/10 px-4 py-2 text-sm font-medium text-m2-primary transition-colors hover:bg-m2-primary/15"
                  >
                    <PlusIcon className="mr-1.5 h-4 w-4" /> Thêm Tài liệu
                  </button>
                </div>
              </div>

              {materialsExpanded && (
                <div className="flex min-h-0 flex-1 flex-col p-3">
                  {loadingMaterials ? (
                    <div className="flex flex-1 items-center justify-center text-m2-on-surface-variant">
                      Đang tải tài liệu...
                    </div>
                  ) : materials.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-m2-outline-variant bg-m2-surface-low p-8 text-m2-on-surface-variant">
                      <DocumentIcon className="mb-4 h-16 w-16 text-m2-outline-variant" />
                      <p className="text-lg">Khóa học này chưa có tài liệu nào.</p>
                      <button
                        onClick={() => setMaterialModalOpen(true)}
                        className="mt-4 rounded-lg bg-m2-primary px-6 py-2.5 text-m2-on-primary transition-colors hover:bg-eh-forest-shadow"
                      >
                        Tải tài liệu lên
                      </button>
                    </div>
                  ) : (
                    <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container shadow-sm">
                      <div className="flex w-1/3 min-h-0 flex-col border-r border-m2-outline-variant bg-m2-surface-low">
                        <div className="shrink-0 border-b border-m2-outline-variant">
                          <div className="flex gap-0.5 overflow-x-auto p-1.5">
                            {MATERIAL_GROUP_TABS.map((tab) => {
                              const count = groupMaterialsByType()[tab]?.length || 0
                              const active = materialGroupTab === tab
                              return (
                                <button
                                  key={tab}
                                  type="button"
                                  onClick={() => setMaterialGroupTab(tab)}
                                  className={`shrink-0 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                                    active
                                      ? 'bg-m2-primary text-m2-on-primary'
                                      : 'text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-on-surface'
                                  }`}
                                >
                                  {tab === 'Tài liệu tham khảo' ? 'Tham khảo' : tab === 'Dự án mẫu' ? 'Dự án' : tab}
                                  <span className={`ml-1 ${active ? 'opacity-90' : 'opacity-70'}`}>
                                    ({count})
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                          <div className="space-y-2 px-2 pb-2">
                            <select
                              value={materialSessionFilter}
                              onChange={(e) => setMaterialSessionFilter(e.target.value)}
                              className="w-full rounded-md border border-m2-outline bg-m2-surface-container py-1.5 px-2 text-sm text-m2-on-surface focus:border-m2-primary focus:outline-none focus:ring-1 focus:ring-m2-primary"
                            >
                              <option value="all">Tất cả buổi / toàn khóa</option>
                              <option value="none">Chỉ tài liệu toàn khóa</option>
                              {courseSessions.map((s) => (
                                <option key={s.id} value={String(s.id)}>
                                  Buổi {s.orderIndex}: {s.title}
                                </option>
                              ))}
                            </select>
                            <div className="relative">
                              <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-m2-on-surface-variant" />
                              <input
                                type="search"
                                value={materialSearch}
                                onChange={(e) => setMaterialSearch(e.target.value)}
                                placeholder={`Tìm trong ${materialGroupTab}...`}
                                className="w-full rounded-md border border-m2-outline bg-m2-surface-container py-1.5 pl-8 pr-2 text-sm text-m2-on-surface placeholder:text-m2-on-surface-variant focus:border-m2-primary focus:outline-none focus:ring-1 focus:ring-m2-primary"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                          {(() => {
                            const fullGroup = groupMaterialsByType()[materialGroupTab] || []
                            const bySession = fullGroup.filter((mat) => {
                              if (materialSessionFilter === 'all') return true
                              if (materialSessionFilter === 'none') return mat.sessionId == null
                              return Number(mat.sessionId) === Number(materialSessionFilter)
                            })
                            const items = filterMaterialsBySearch(bySession)
                            if (items.length === 0) {
                              return (
                                <p className="p-4 text-center text-sm text-m2-on-surface-variant">
                                  {materialSearch.trim()
                                    ? `Không tìm thấy “${materialSearch.trim()}”`
                                    : `Chưa có tài liệu trong ${materialGroupTab}`}
                                </p>
                              )
                            }
                            return items.map((mat) => {
                              const index = fullGroup.findIndex((m) => m.id === mat.id)
                              const sessionLabel = mat.sessionTitle
                                ? `Buổi ${mat.sessionOrderIndex ?? '?'}: ${mat.sessionTitle}`
                                : mat.sessionId
                                  ? `Buổi #${mat.sessionId}`
                                  : 'Toàn khóa'
                              return (
                                <div
                                  key={mat.id}
                                  onClick={() => setSelectedMaterial(mat)}
                                  className={`flex cursor-pointer flex-col border-b border-m2-outline-variant p-3 transition-colors hover:bg-m2-primary/10 ${
                                    selectedMaterial?.id === mat.id
                                      ? 'border-l-4 border-l-m2-primary bg-m2-primary/15'
                                      : ''
                                  }`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex max-w-[70%] items-start space-x-3">
                                      {String(mat.type || '').toUpperCase() === 'SAMPLE_PROJECT' ? (
                                        <FolderIcon className="mt-1 h-5 w-5 shrink-0 text-amber-600" />
                                      ) : (
                                        <DocumentIcon className="mt-1 h-5 w-5 shrink-0 text-m2-on-surface-variant" />
                                      )}
                                      <div>
                                        <p
                                          className={`line-clamp-2 font-medium ${
                                            selectedMaterial?.id === mat.id
                                              ? 'text-m2-primary'
                                              : 'text-m2-on-surface'
                                          }`}
                                        >
                                          {mat.title}
                                        </p>
                                        <p className="mt-1 text-[10px] uppercase text-m2-on-surface-variant">
                                          {String(mat.type || '').toUpperCase() === 'SAMPLE_PROJECT'
                                            ? 'FOLDER'
                                            : mat.fileType || 'FILE'}
                                        </p>
                                        <p
                                          className={`mt-1 text-[10px] font-medium ${
                                            mat.sessionId ? 'text-m2-primary' : 'text-m2-on-surface-variant'
                                          }`}
                                        >
                                          {sessionLabel}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex shrink-0 items-center space-x-1 rounded-md border border-m2-outline-variant bg-m2-surface-container p-0.5 shadow-sm">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleMoveMaterial(mat.id, 'up')
                                        }}
                                        disabled={index <= 0}
                                        className={`rounded p-1.5 ${
                                          index <= 0
                                            ? 'text-m2-outline-variant'
                                            : 'text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-primary'
                                        }`}
                                        title="Di chuyển lên"
                                      >
                                        <ArrowUpIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleMoveMaterial(mat.id, 'down')
                                        }}
                                        disabled={index < 0 || index >= fullGroup.length - 1}
                                        className={`rounded p-1.5 ${
                                          index < 0 || index >= fullGroup.length - 1
                                            ? 'text-m2-outline-variant'
                                            : 'text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-primary'
                                        }`}
                                        title="Di chuyển xuống"
                                      >
                                        <ArrowDownIcon className="h-4 w-4" />
                                      </button>
                                      <div className="mx-1 h-4 w-px bg-m2-surface-highest"></div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleDeleteMaterial(mat.id)
                                        }}
                                        className="rounded p-1.5 text-red-500 hover:bg-m2-error/10"
                                        title="Xóa"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )
                            })
                          })()}
                        </div>
                      </div>

                      <div className="w-2/3 overflow-y-auto bg-m2-surface-high p-4">
                        {selectedMaterial ? (
                          <div className="flex h-full min-h-0 flex-col gap-3">
                            <div className="shrink-0 rounded-lg border border-m2-outline-variant bg-m2-surface-container p-3">
                              <label className="mb-1 block text-xs font-medium text-m2-on-surface-variant">
                                Gắn theo buổi học
                              </label>
                              <select
                                value={
                                  selectedMaterial.sessionId != null
                                    ? String(selectedMaterial.sessionId)
                                    : ''
                                }
                                disabled={updatingMaterialSession}
                                onChange={(e) =>
                                  handleUpdateMaterialSession(selectedMaterial.id, e.target.value)
                                }
                                className="w-full rounded-md border border-m2-outline bg-m2-surface-container py-2 px-2 text-sm text-m2-on-surface focus:border-m2-primary focus:outline-none focus:ring-1 focus:ring-m2-primary disabled:opacity-60"
                              >
                                <option value="">Toàn khóa (không gắn buổi cụ thể)</option>
                                {courseSessions.map((s) => (
                                  <option key={s.id} value={String(s.id)}>
                                    Buổi {s.orderIndex}: {s.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="min-h-0 flex-1 flex flex-col">
                              {String(selectedMaterial.type || '').toUpperCase() === 'SAMPLE_PROJECT' ? (
                                <SampleProjectViewer
                                  courseId={Number(id)}
                                  materialId={selectedMaterial.id}
                                  title={selectedMaterial.title}
                                  admin
                                />
                              ) : (
                                <>
                                  <div className="min-h-[280px] flex-1 overflow-hidden">
                                    <DocumentViewer
                                      fileUrl={selectedMaterial.fileUrl}
                                      fileType={selectedMaterial.fileType || ''}
                                      title={selectedMaterial.title}
                                      materialId={selectedMaterial.id}
                                      courseId={Number(id)}
                                      canAnnotate
                                      compact
                                      onWhiteboardSaved={() => setWhiteboardRefresh((k) => k + 1)}
                                      editRequest={
                                        whiteboardEditRequest?.materialId === selectedMaterial.id
                                          ? whiteboardEditRequest
                                          : null
                                      }
                                      onEditRequestHandled={() => setWhiteboardEditRequest(null)}
                                    />
                                  </div>
                                  <div className="shrink-0 border-t border-m2-outline-variant px-1 pb-2">
                                    <MaterialWhiteboardList
                                      courseId={Number(id)}
                                      materialId={selectedMaterial.id}
                                      refreshKey={whiteboardRefresh}
                                      onEditWhiteboard={setWhiteboardEditRequest}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex h-full flex-col items-center justify-center text-m2-outline">
                            <DocumentIcon className="mb-4 h-16 w-16 text-m2-outline-variant" />
                            <p>Chọn một tài liệu ở danh sách bên trái để xem</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Bộ skill đính kèm */}
            <section
              className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-m2-outline-variant bg-m2-surface-container ${
                skillsExpanded
                  ? materialsExpanded
                    ? 'max-h-[38%] min-h-[160px] flex-1'
                    : 'min-h-0 flex-1'
                  : 'shrink-0'
              }`}
            >
              <CourseSkillAttach
                courseId={courseId}
                expanded={skillsExpanded}
                onToggleExpanded={() => setSkillsExpanded((v) => !v)}
                fillHeight={skillsExpanded}
              />
            </section>
          </div>
        )}
      </div>

      <CourseContentEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={fetchContent}
        courseId={courseId}
        initialData={editingItem}
        contentType={selectedContentType}
        parentId={selectedParentId}
      />

      <CourseContentCopyFromModal
        isOpen={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        onCopied={handleCopySuccess}
        courseId={courseId}
        targetParentId={copyTargetParentId}
      />

      <CourseMaterialModal
        isOpen={materialModalOpen}
        onClose={() => setMaterialModalOpen(false)}
        onSaved={fetchMaterials}
        courseId={courseId}
        sessions={courseSessions}
        defaultSessionId={
          materialSessionFilter !== 'all' && materialSessionFilter !== 'none'
            ? Number(materialSessionFilter)
            : null
        }
      />
    </div>
  )
}

export default AdminCourseDetailPage
